import { evaluate } from 'mathjs';
import { black, productStates, white } from '../utilities';
import {
  currency,
  dataKeyBeginningDelimiter,
  dataKeyEndingDelimiter,
  emptyRowStateJsonMessage,
  func,
  leadingAndTrailingDelimiterRegEx,
  ruleEvaluationMessage,
  ruleStyleMissingMessage,
} from './constants';

export const isJson = (item) => {
  item = typeof item !== 'string' ? JSON.stringify(item) : item;

  try {
    item = JSON.parse(item);
  } catch (e) {
    return false;
  }

  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
    return true;
  }

  return false;
};

export const isFunction = (fn) => typeof fn === 'function';

export const isPromise = (fn) =>
  Object.prototype.toString.call(fn) === '[object Promise]';

export const invoke = (fn, args) => {
  if (!fn) return false;
  fn = isFunction(fn) ? fn : window[fn];

  const result = fn.apply(this, args || []);

  // if not a promise object, nor a mobx action (store method)
  if (!isPromise(result) && !fn?.isMobxAction) {
    const hasBadResult = result.indexOf('undefined') > -1;
    if (hasBadResult)
      throw new Error('Invalid argument passed to invoke method', result);
  }

  if (isPromise(result)) return result;

  return result === undefined ? true : result;
};

export const isCurrencyText = (value) =>
  typeof value === 'string' && value.toLowerCase() === currency;

export const isInFirstPosition = (str, chr) =>
  typeof str === 'string' && str.substr(0, 1) === chr?.toString();

export const isInLastPosition = (str, chr) =>
  typeof str === 'string' && str.substr(str?.length - 1, 1) === chr?.toString();

export const replaceStringValue = (str, regexPattern, replacementValue) =>
  typeof str === 'string' && str.replace(regexPattern, replacementValue);

export const isDataKey = (value) => {
  // data key must be a string and have at least one character
  if (typeof value !== 'string' || value?.length < 3) return false;

  // grab value inside delims
  const innerValue = value.substring(1, value.length - 1);

  // data key cannot contain these chars
  const invalidChars = [' ', '{', '}', '[', ']'];
  const hasInvalidChars =
    invalidChars.filter((char) => innerValue.indexOf(char) > -1).length > 0;
  if (hasInvalidChars) return false;

  // data key aside from delims, must not be numeric
  if (!isNaN(innerValue)) return false;

  return (
    isInFirstPosition(value, dataKeyBeginningDelimiter) &&
    isInLastPosition(value, dataKeyEndingDelimiter)
  );
};

export const getDataValue = (data, key = null) => {
  // console.log('getDataValue', {data, key})
  if (!isJson(data)) throw emptyRowStateJsonMessage;

  const { row, state } = data;
  if (!row && !state) throw emptyRowStateJsonMessage;
  if (key === null || !isNaN(key) || typeof key !== 'string')
    throw 'Invalid key. Must be a string';

  if (row && isJson(row) && row.hasOwnProperty(key)) return row[key];
  if (state && isJson(state) && state.hasOwnProperty(key)) return state[key];

  return isDataKey(key)
    ? row[replaceStringValue(key, leadingAndTrailingDelimiterRegEx, '')]
    : row[key];
};

export const getDataValueFromJson = (state, data) => {
  // no data arg or not in json format
  if (!data || !isJson(data)) return null;

  // data object doesn't have row property
  if (!data.hasOwnProperty('row')) return null;

  // row property is null or an empty array
  if (!data.row || !isJson(data.row)) return null;

  // state is null or not a json object
  if (!state || !isJson(state)) return null;

  const arr = [];
  // iterate over state object and build new object with row data as props/values
  for (let [key, value] of Object.entries(state)) {
    const kv = getDataValue(data, value);
    if (kv) {
      arr.push([[key], kv || value]);
    }
  }

  return Object.fromEntries(arr);
};

export const concatArgsWithValuesAsString = (args = [], data) => {
  if (typeof args === 'string' || typeof args === 'number')
    return args.toString();
  if (!args || !Array.isArray(args)) return '';

  return args
    .map((arg) => {
      const value = getDataValue(data, arg);
      return !value ? arg : value;
    })
    .join('');
};

export const getUrlFromArrayOrProp = (args = [], data) => {
  if (typeof args === 'string' || typeof args === 'number')
    return `/${args.toString()}`;
  if (!args || !Array.isArray(args)) return '/';

  const url = args.map((part) =>
    isDataKey(part) ? getDataValue(data, part) : part,
  );
  return Array.isArray(url) ? `/${url.join('/')}` : `/${url}`;
};

// FIXME: this function does too many things. Consider breaking it up into 3 separate functions (SRP)
// different sets of parameters can come into this making it confusing. ***Refactor***
export const getButtonUrlFromArgsAndData = (
  url,
  fn = null,
  args = [],
  data,
) => {
  const { row, state } = data;
  const urlArgValues = args?.map((arg) => state[arg] || row[arg]) || [];

  // console.log('urlArgValues', urlArgValues, args)

  // BLOCK 1
  if (url === func && !isFunction(fn)) {
    const result = () => invoke(state[fn].fn, urlArgValues);
    // console.log('getButtonUrlFromArgsAndData > one', {url, data, result, urlArgValues})
    // console.log('result', {state: JSON.stringify(state), fn, urlArgValues});
    return result;
    // console.log('getButtonUrlFromArgsAndData > one', invoke(state[fn].fn, urlArgValues))
    // console.log('getButtonUrlFromArgsAndData', {
    //   row,
    //   state,
    //   url,
    //   fn,
    //   args,
    //   data,
    //   urlArgValues,
    //   isFunction: isFunction(fn),
    //   onClickFunc: state[fn].fn,
    // });
    //return () => invoke(state[fn].fn, urlArgValues);
  }

  // BLOCK 2
  if (url === func && isFunction(fn)) {
    // console.log('getButtonUrlFromArgsAndData > two', invoke(fn, urlArgValues))
    return () => invoke(fn, urlArgValues);
  }

  // BLOCK 3
  const result = getUrlFromArrayOrProp(url, data);
  // console.log('getButtonUrlFromArgsAndData > three', {url, data, result, urlArgValues})
  return result; //getUrlFromArrayOrProp(url, data);
};

export const getSortDirection = (direction) =>
  direction === 'asc' ? 'ascending' : 'descending';

export const getSortDirectionForColumn = (sortedColumn, column, direction) => {
  if (sortedColumn !== column) return 'asc';
  return ['ascending', 'asc'].includes(direction) ? 'desc' : 'asc';
};

export const getProductStateColor = (productState) => {
  const state = productState?.toUpperCase().replace(' ', '');
  const { noSale, open, pulled, sold, subjectTo } = productStates;
  if (state === 'SOLD') return { bgColor: sold, color: white };
  if (state === 'NOSALE') return { bgColor: noSale, color: white };
  if (state === 'SUBJECTTO') return { bgColor: subjectTo, color: black };
  if (state === 'OPEN') return { bgColor: open, color: white };
  if (state === 'PULLED') return { bgColor: pulled, color: white };
  return { bgColor: 'inherit', color: 'inherit' };
};

export const random = (min, max, rnd = Math.random()) =>
  Math.floor(rnd * (max - min)) + min;

export const generateKey = (prefix, value = new Date().getTime()) =>
  `${prefix}_${value}`;

export const getOrdinalValueWithSuffix = (number) =>
  `${number}${getOrdinalSuffix(number)}`;

export const getOrdinalSuffix = (number) => {
  if (typeof number !== 'number')
    throw "Invalid number argument. Must be of type 'number'";
  if (number % 10 === 1 && number !== 11) return 'st';
  if (number % 10 === 2 && number !== 12) return 'nd';
  if (number % 10 === 3 && number !== 13) return 'rd';
  return 'th';
};

export const getRuleStyleExceptionMessage = (message, shape, value) => {
  const example = {
    style: [
      {
        expr: 'x == 1.5',
        scope: {
          x: '<any_since_this_value_gets_set_in_function>',
        },
        style: {
          backgroundColor: 'green',
          color: 'white',
        },
      },
    ],
  };
  const shapeAsString = JSON.stringify(example, null, ' ');
  return `${message}. 
  
  ** Make sure to scroll this error message and check the console log for detailed information to remedy issue **
  
  Current style: ${JSON.stringify(shape, null, ' ')}
  Current Value: ${value}
  
  Valid Example Style: ${shapeAsString}
  Where the following is REQUIRED: 
  1. expr where "x [==, >, <, ...] some value" (represented as a string) so that it evaluates to true/false. 
  2. scope object with property of "x". Its value does not matter in the object, as we set it to the value that is passed into the function.
  3. style object with valid JSX CSS property and values exist`;
};

export const getRuleStyle = (style = [], value = Infinity) => {
  if (typeof value !== 'number') value = Infinity;
  const ruleWeWant =
    style?.filter((rule) => {
      const scope = { ...rule?.scope };
      scope.x = value;
      try {
        return evaluate(rule?.expr, scope);
      } catch (error) {
        console.error(error);
        throw new Error(
          getRuleStyleExceptionMessage(ruleEvaluationMessage, style, value),
        );
      }
    }) || [];

  if (ruleWeWant.length && !ruleWeWant[0].hasOwnProperty('style')) {
    console.error(
      'Missing style on ruleStyle object',
      JSON.stringify(ruleWeWant, null, ' '),
    );
    throw new Error(
      getRuleStyleExceptionMessage(ruleStyleMissingMessage, style, value),
    );
  }

  return ruleWeWant.length ? ruleWeWant[0].style : null;
};
